import { useMemo } from "react";
import styled from "styled-components";
import dayjs from "dayjs";

import { device } from "@constants/styles";
import { useMyTranslation } from "@components/Lang";

// Wi-Fi module reset history, drawn as a timeline.
//
// The module stamps a reset REASON CODE (0-6) on the operation report's `event`
// rows; ordinary rows carry `reset: null`. So the feed is just the operation
// event rows with a real code, newest first.
//
// Note 0 is a meaningful code ("normal startup by power on"), not "no value" —
// hence the explicit null/"" checks rather than a falsy test.

export interface WifiEvent {
  time: string;
  reset: number;
}

const RESET_MIN = 0;
const RESET_MAX = 6;

// codes with a follow-up line (`wifireset<N>detail`) under the main sentence
// every code now carries a follow-up line
const DETAIL_CODES = new Set([0, 1, 2, 3, 4, 5, 6]);

export const toWifiEvents = (rows: any[]): WifiEvent[] =>
  (rows ?? [])
    .filter((r: any) => {
      const raw = r?.reset;
      if (raw === null || raw === undefined || raw === "") return false;
      const n = Number(raw);
      return Number.isInteger(n) && n >= RESET_MIN && n <= RESET_MAX;
    })
    .map((r: any) => ({ time: r?.time ?? "", reset: Number(r.reset) }))
    // newest first
    .sort((a, b) => String(b.time).localeCompare(String(a.time)));

// Tabler glyphs, inlined the same way the metric pages inline theirs — the
// icon set isn't a dependency here.
// plug-connected, not the power button: this code means mains power reached
// the module, not that someone pressed anything
const PlugConnectedIcon = () => (
  <>
    <path d="M7 12l5 5l-1.5 1.5a3.536 3.536 0 1 1 -5 -5l1.5 -1.5z" />
    <path d="M17 12l-5 -5l1.5 -1.5a3.536 3.536 0 1 1 5 5l-1.5 1.5z" />
    <path d="M3 21l2.5 -2.5" />
    <path d="M18.5 5.5l2.5 -2.5" />
    <path d="M10 11l-2 2" />
    <path d="M13 14l-2 2" />
  </>
);
const CpuIcon = () => (
  <>
    <path d="M5 5m0 1a1 1 0 0 1 1 -1h12a1 1 0 0 1 1 1v12a1 1 0 0 1 -1 1h-12a1 1 0 0 1 -1 -1z" />
    <path d="M9 9h6v6h-6z" />
    <path d="M3 10l2 0" />
    <path d="M3 14l2 0" />
    <path d="M10 3l0 2" />
    <path d="M14 3l0 2" />
    <path d="M21 10l-2 0" />
    <path d="M21 14l-2 0" />
    <path d="M10 21l0 -2" />
    <path d="M14 21l0 -2" />
  </>
);
const WifiOffIcon = () => (
  <>
    <path d="M12 18l.01 0" />
    <path d="M9.172 15.172a4 4 0 0 1 5.656 0" />
    <path d="M6.343 12.343a7.963 7.963 0 0 1 3.864 -2.14m4.163 .155a7.965 7.965 0 0 1 3.287 2" />
    <path d="M3.515 9.515a12 12 0 0 1 3.544 -2.455m3.101 -.92a12 12 0 0 1 10.325 3.374" />
    <path d="M3 3l18 18" />
  </>
);
// Code 3's glyph went through a clock (a misreading — it is a watchdog, not a
// timeout) and then `< / >`, which reads as "source code" only to someone who
// writes it. Both Thai sentences call these a **ระบบป้องกัน**, so a shield is
// the word the reader already has, and the tick says it worked. Which watchdog
// caught it — hardware or software — is a distinction no glyph will carry to
// an operator, and the sentence names it outright anyway.
const ShieldCheckIcon = () => (
  <>
    <path d="M9 12l2 2l4 -4" />
    <path d="M12 3a12 12 0 0 0 8.5 3a12 12 0 0 1 -8.5 15a12 12 0 0 1 -8.5 -15a12 12 0 0 0 8.5 -3" />
  </>
);
const RefreshIcon = () => (
  <>
    <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
    <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
  </>
);
// A sunrise was a metaphor for waking; this names the actual subject, power
// saving. Same choice as code 2's wifi-off: the glyph states the CONDITION the
// entry is about and the text says what happened to it. Safe to read as a
// state here because code 5 is only information — nothing to act on.
const SleepIcon = () => (
  <>
    <path d="M4 12h6l-6 8h6" />
    <path d="M14 4h6l-6 8h6" />
  </>
);
// Code 6 is an external reset SIGNAL, and the copy names electrical noise as
// the cause. This went through a power symbol (which meant code 0's story) and
// a bolt, which said "electrical" but not "noise" — and as a hollow shape it
// was nearly all diagonal, so at 18px the outline closed up into a squiggle.
// A spiking trace says both at once: a line that should be steady, disturbed.
// It also survives the size, being mostly long horizontal strokes.
const SpikeIcon = () => (
  <>
    <path d="M3 12h4l3 8l4 -16l3 8h4" />
  </>
);

// Colour carries SEVERITY — what the reader should do about it — while the
// glyph still says what happened. Severity is derived from the code itself, so
// nothing new is needed from the device.
//
// A fourth level ("action required", red) is meant for repeated restarts; that
// needs a rule over the whole list rather than a per-code lookup, so it is not
// implemented yet.
type Severity = "info" | "recovery" | "check";

const SEVERITY: Record<number, Severity> = {
  // A cabinet is never switched off in normal use — only for maintenance — so a
  // power-up almost always means the mains dropped. That is exactly what an
  // operator wants flagged, hence "check" rather than "info".
  0: "check", // started on power-up
  1: "recovery", // hardware watchdog
  2: "check", // interrupted operation / low supply voltage
  3: "recovery", // software watchdog
  4: "info", // scheduled / configured restart
  5: "info", // resumed from power saving
  // an external reset signal isn't a command anyone issued — the hardware team
  // expects this only under strong electrical noise, and the entry now tells
  // the reader to inspect power and grounding, so green would contradict it
  6: "check", // external reset signal
};

const SEVERITY_STYLE: Record<
  Severity,
  { color: string; bg: string; labelKey: string }
> = {
  info: { color: "#16a34a", bg: "#ecfdf5", labelKey: "detail.wifisevinfo" },
  recovery: {
    color: "#3b82f6",
    bg: "#eff6ff",
    labelKey: "detail.wifisevrecovery",
  },
  check: { color: "#f97316", bg: "#fff7ed", labelKey: "detail.wifisevcheck" },
};

const RESET_GLYPH: Record<number, () => JSX.Element> = {
  0: PlugConnectedIcon,
  1: CpuIcon,
  2: WifiOffIcon,
  3: ShieldCheckIcon,
  4: RefreshIcon,
  5: SleepIcon,
  6: SpikeIcon,
};

const InfoIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#64748b"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
    <path d="M12 9h.01" />
    <path d="M11 12h1v4h1" />
  </svg>
);

export default function WifiEventTimeline({
  events = [],
}: {
  events: WifiEvent[];
}) {
  const { t } = useMyTranslation();
  // One date heading per day instead of repeating the date on every row — a
  // device that restarts a dozen times in a day would otherwise print the same
  // date a dozen times. `events` is already newest-first, so runs of the same
  // day are adjacent and a single pass groups them.
  const groups = useMemo(() => {
    const out: { date: string; items: WifiEvent[] }[] = [];
    events.forEach((e) => {
      const d = dayjs(e.time);
      const date = d.isValid() ? d.format("DD/MM/YYYY") : "-";
      const last = out[out.length - 1];
      if (last && last.date === date) last.items.push(e);
      else out.push({ date, items: [e] });
    });
    return out;
  }, [events]);
  return (
    <Wrap>
      {/* The card title already says what this list is, so the only thing worth
          writing here is what a reader would otherwise get wrong: a restart
          entry is not damage. */}
      {/* Two bullets rather than a paragraph plus a divided afterthought: they
          are two separate things to know before reading the list, and a rule
          drawn under a single trailing line looked like a section break for no
          section. The firmware one stays lighter — it is a prerequisite, not
          the point. */}
      <ul className="wev__desc size-12 font-sukhumvitSet-text">
        <li>
          {t("detail.wifieventnote")}{" "}
          <strong className="wev__desc-strong">
            {t("detail.wifieventnotestrong")}
          </strong>
        </li>
        <li className="wev__fw">{t("detail.wifieventfw")}</li>
      </ul>
      {events.length === 0 ? (
        <div className="wev__empty size-14 font-sukhumvitSet-text">
          <InfoIcon />
          {t("detail.wifieventempty")}
        </div>
      ) : (
        <div className="wev__list">
          {groups.map((g) => (
            <div className="wev__group" key={g.date}>
              <div className="wev__day size-12 font-sukhumvitSet-text">
                {g.date}
              </div>
              {g.items.map((e, i) => {
                const sev = SEVERITY[e.reset] ?? "info";
                const style = SEVERITY_STYLE[sev];
                const Glyph = RESET_GLYPH[e.reset] ?? RESET_GLYPH[6];
                const d = dayjs(e.time);
                return (
                  <div className="wev__row" key={`${e.time}-${i}`}>
                <div className="wev__marker">
                  <span
                    className="wev__badge"
                    style={{ background: style.color }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#ffffff"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <Glyph />
                    </svg>
                  </span>
                </div>
                <div className="wev__body">
                  {/* Only the levels that ask something of the reader get a
                      label. Tagging the routine ones too would put a chip on
                      almost every row, which is how a label stops meaning
                      anything — the badge colour already carries "normal". */}
                  {sev === "info" ? null : (
                    <span
                      className="wev__sev size-12 font-sukhumvitSet-text"
                      style={{ color: style.color, background: style.bg }}
                    >
                      {t(style.labelKey as any)}
                    </span>
                  )}
                  <div className="size-14 font-sukhumvitSet-text color-black wev__reason">
                    {t(`detail.wifireset${e.reset}` as any)}
                  </div>
                  {DETAIL_CODES.has(e.reset) ? (
                    <div className="wev__detail size-12 font-sukhumvitSet-text">
                      {t(`detail.wifireset${e.reset}detail` as any)}
                    </div>
                  ) : null}
                </div>
                {/* the date lives in the group header now, so a row only
                    carries its own time */}
                <div className="wev__when">
                  <div className="size-14 font-sukhumvitSet-text wev__time">
                    {d.isValid() ? d.format("HH:mm:ss") : "-"}
                  </div>
                  {/* the raw reset code, kept quiet: customers can ignore it,
                      support can quote it to the factory engineers */}
                  <div className="wev__code size-12 font-sukhumvitSet-text">
                    {t("detail.wifieventcode")} {e.reset}
                  </div>
                </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </Wrap>
  );
}

const Wrap = styled.div`
  /* Fills the card, like the charts on every other tab. This was capped at
     1120px to keep the timestamps near the text they belong to, but on a wide
     screen the cap left the card half empty and read as broken layout rather
     than a deliberate column — and the activity-log table on this same page
     already runs its last column to the edge. */
  width: 100%;
  margin: 18px 0 0;
  .wev {
    &__desc {
      padding: 12px 16px;
      margin: 0;
      background: #f8fafc;
      border: 1px solid #eef2f7;
      border-radius: 10px;
      color: #64748b;
      line-height: 1.65;
      list-style: none;
      > li {
        position: relative;
        padding-left: 14px;
        /* own dot rather than list-style, so it takes the line's colour and
           the lighter second bullet dims with its text */
        &::before {
          content: "";
          position: absolute;
          left: 2px;
          top: 0.72em;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: currentColor;
        }
        + li {
          margin: 4px 0 0;
        }
      }
    }
    /* the reassurance is the sentence that has to land, so it gets weight and a
       darker tone against the muted body */
    &__desc-strong {
      color: #334155;
      font-weight: 700;
    }
    /* the prerequisite bullet, kept lighter so the first one still leads */
    &__fw {
      color: #94a3b8;
    }
    &__empty {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      min-height: 220px;
      margin: 18px 0 0;
      /* same dashed box ChartPlaceholder uses, so "nothing to show" looks the
         same everywhere — without it the blank area reads as a broken card */
      border: 1px dashed #e2e8f0;
      border-radius: 12px;
      color: #64748b;
    }
    &__list {
      /* no inner scroll: nesting one inside the page's own means aiming the
         cursor before the wheel does what you expect. The card grows and the
         page scrolls, which is how a log is normally read. */
      padding: 8px 4px 0;
    }
    &__row {
      position: relative;
      display: flex;
      align-items: flex-start;
      gap: 14px;
      padding: 0 4px 14px;
      &:not(:last-child) {
        margin: 0 0 14px;
      }
      /* hairline between rows, starting after the icon column so it separates
         the text without cutting across the timeline's own line */
      &:not(:last-child)::before {
        content: "";
        position: absolute;
        left: 52px;
        right: 0;
        bottom: -1px;
        height: 1px;
        background: #f4f6f9;
      }
      /* connector line between badges — not after the last one. It has to span
         the row's full height plus the gap, otherwise a three-line entry leaves
         the line hanging short of the next badge. */
      &:not(:last-child) .wev__marker::after {
        content: "";
        position: absolute;
        left: 50%;
        top: 38px;
        bottom: -28px;
        width: 2px;
        transform: translateX(-50%);
        background: #e9eef5;
      }
    }
    &__marker {
      position: relative;
      flex: none;
      width: 34px;
      display: flex;
      justify-content: center;
      /* full row height, so the connector above can measure from its bottom */
      align-self: stretch;
    }
    &__badge {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    &__body {
      flex: 1;
      min-width: 0;
      padding: 4px 0 0;
    }
    &__sev {
      display: inline-block;
      padding: 2px 10px;
      margin: 0 0 6px;
      border-radius: 999px;
      font-weight: 600;
      line-height: 1.5;
    }
    &__reason {
      line-height: 1.45;
    }
    &__detail {
      margin: 4px 0 0;
      color: #64748b;
      line-height: 1.55;
    }
    &__when {
      flex: none;
      padding: 4px 0 0;
      text-align: right;
    }
    &__day {
      margin: 16px 0 12px;
      color: #94a3b8;
      font-weight: 600;
    }
    &__group:first-child .wev__day {
      margin-top: 2px;
    }
    &__time {
      color: #1f2328;
      white-space: nowrap;
    }
    &__code {
      margin: 4px 0 0;
      color: #94a3b8;
      white-space: nowrap;
    }
    @media ${device.mobileLL} {
      &__row {
        gap: 10px;
      }
      &__time {
        font-size: 12px;
      }
    }
  }
`;
